import { createFeatureSelector, createSelector } from '@ngrx/store';
import { memoize } from 'lodash';
import { messageConversationEntityAdapter } from '../adapters/message-conversation.adapter';
import { GenesysCloudMessageConversationsState } from '../genesys-cloud-message-conversations.state';
import { featureKey } from '../reducers/feature-key';
import { Message, Participant } from 'common/store/genesys-cloud/conversations/common/models/participant.model';
import { ConnectionState } from 'common/store/genesys-cloud/conversations/common/models/connection-state.enum';
import { wrapupRequiredValues } from 'common/store/genesys-cloud/conversations/common/helpers/wrap-up-required-values';
import { MessageConversation } from '../models/message-conversation.model';
import { MessageType } from '../../common/models/message-type.enum';

const { selectAll, selectEntities, selectIds } = messageConversationEntityAdapter.getSelectors();

export const selectState = createFeatureSelector<GenesysCloudMessageConversationsState>(featureKey);
const selectWrapUpsSubState = createSelector(selectState, (state) => state.wrapups);
const selectConversationsSubState = createSelector(selectState, (state) => state.conversations);
const selectAllMessageConversations = createSelector(selectState, (state) => selectAll(state.conversations));
const selectConversationEntities = createSelector(selectConversationsSubState, selectEntities);
export const selectAllMessageConversationIds = createSelector(
  selectConversationsSubState,
  (subState) => <string[]>selectIds(subState)
);

export const selectActiveMessageConversations = createSelector(selectAllMessageConversations, (conversations) =>
  conversations.filter((conversation) => conversation.isActive)
);

export const selectActiveMessageConversationCount = createSelector(
  selectActiveMessageConversations,
  (conversations) => conversations.length
);

export const selectMessageConversationById = memoize((conversationId: string) =>
  createSelector(selectConversationEntities, (entities) => entities[conversationId])
);

export const selectWrapUpsByConversationId = memoize((conversationId: string) =>
  createSelector(selectWrapUpsSubState, (entities) => entities[conversationId] || [])
);

const selectAgentParticipant = memoize((conversationId: string) =>
  createSelector(selectMessageConversationById(conversationId), (conversation) => conversation?.agentParticipant)
);

export const selectCommunicationId = memoize((conversationId: string) =>
  createSelector(selectAgentParticipant(conversationId), getCommunicationId)
);

export const selectMessageConversationTypeById = memoize((conversationId: string) =>
  createSelector(
    selectMessageConversationById(conversationId),
    (conversation) =>
      getTypeFromParticipantMessages(conversation?.customerParticipant) ||
      getTypeFromParticipantMessages(conversation?.agentParticipant)
  )
);

export const selectWrapupPrompt = memoize((conversationId: string) =>
  createSelector(selectMessageConversationById(conversationId), (conversation) =>
    wrapupRequiredValues.has(conversation.agentParticipant?.wrapupPrompt)
  )
);

export const selectMessageConversationStateIsOpen = memoize((conversationId: string) =>
  createSelector(selectMessageConversationById(conversationId), (conversation) =>
    conversation.agentParticipant?.messages?.some((e) => e.state !== ConnectionState.Disconnected)
  )
);

export const selectMessageConversationIsAssignedByQueue = memoize((conversationId: string) =>
  createSelector(selectMessageConversationById(conversationId), (conversation) => isAssignedByQueue(conversation))
);

function hasCommunicationId(message: Message): boolean {
  return (
    message.state.toUpperCase() === ConnectionState.Connected.toUpperCase() ||
    message.state.toUpperCase() === ConnectionState.Disconnected.toUpperCase()
  );
}

function getTypeFromParticipantMessages(participant: Participant): MessageType {
  return participant?.messages.find((m) => m?.type)?.type;
}

function getCommunicationId(agentParticipant: Participant): string {
  return agentParticipant?.messages.find((m) => hasCommunicationId(m) && !m.wrapup)?.id;
}

function isAssignedByQueue(conversation: MessageConversation) {
  const acdParticipant = conversation?.acdParticipants?.find((p) => !p.endTime);
  return acdParticipant?.messages?.some((m) => m.state !== ConnectionState.Disconnected);
}
